import { memo, useEffect, useRef, useState } from 'react'
import type { PFFT } from '../vendor/pffft/pretty-fast-fft.ts'
import { animationManager } from './animation-manager.ts'
import { delayBufferManager } from './delay-buffer-manager.ts'
import { dspscript } from './dsp-script.ts'
import { useVisualizerStore } from './store.ts'

export const Visualizers = memo(function Visualizers({
  isPlaying,
  layout = 'grid',
  mode,
}: {
  isPlaying: boolean
  layout?: 'grid' | 'vertical'
  mode?: 0 | 1 | 2 | 3
}) {
  // Observes container size so canvases can match when layout changes without window resize
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRefs = [
    useRef<HTMLCanvasElement>(null),
    useRef<HTMLCanvasElement>(null),
    useRef<HTMLCanvasElement>(null),
    useRef<HTMLCanvasElement>(null),
  ]
  const canvasContextRef = useRef<CanvasRenderingContext2D | null>(null)
  const delayNodeRef = useRef<DelayNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const freqArrayRef = useRef<Uint8Array | null>(null)
  const transientDataArraysRef = useRef<Float32Array[]>([])
  const stereoDataArraysRef = useRef<{ left: Uint8Array; right: Uint8Array }[]>([])
  const initializedRef = useRef(false)
  const delayBufferIdRef = useRef('visualizers')
  const delayBufferReadPtrRef = useRef<number[]>([])
  const delayBufferFillLevelRef = useRef<number[]>([])
  const lastScrollTimeRef = useRef<(number | null)[]>([])
  const scrollRateRef = useRef<number[]>([])
  const animatedHeightsRef = useRef<Float32Array[]>([])
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [hoveredFreq, setHoveredFreq] = useState<{ index: number; hz: number } | null>(null)
  const { verticalIndex, cycleVerticalIndex } = useVisualizerStore()
  const resizeFnRef = useRef<(() => void) | null>(null)
  const modeRef = useRef(mode)
  const expandedIndexRef = useRef(expandedIndex)
  const isVerticalRef = useRef(layout === 'vertical')
  const verticalIndexRef = useRef(verticalIndex)

  const isVertical = layout === 'vertical'

  // Keep refs in sync
  modeRef.current = mode
  expandedIndexRef.current = expandedIndex
  isVerticalRef.current = isVertical
  verticalIndexRef.current = verticalIndex

  useEffect(() => {
    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      canvasRefs.forEach((ref, i) => {
        const canvas = ref.current
        if (!canvas) return
        const { clientWidth, clientHeight } = canvas
        canvas.width = Math.max(1, Math.floor(clientWidth * dpr))
        canvas.height = Math.max(1, Math.floor(clientHeight * dpr))
        if (i % 4 === 1 && !canvasContextRef.current) {
          const ctx = canvas.getContext('2d', { willReadFrequently: true })
          if (ctx) {
            ctx.imageSmoothingEnabled = false
            canvasContextRef.current = ctx
          }
        }
      })
    }

    resizeFnRef.current = resize

    const scheduleResize = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resize)
      })
    }

    scheduleResize()
    window.addEventListener('resize', resize)

    // Track container size changes that don't emit window resize (e.g. route/layout switches)
    const ro = containerRef.current
      ? new ResizeObserver(() => {
        scheduleResize()
      })
      : null
    if (ro && containerRef.current) ro.observe(containerRef.current)

    return () => {
      window.removeEventListener('resize', resize)
      if (ro && containerRef.current) ro.unobserve(containerRef.current)
    }
  }, [layout])

  useEffect(() => {
    if (resizeFnRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resizeFnRef.current?.()
        })
      })
    }
  }, [layout, verticalIndex])

  useEffect(() => {
    return () => {
      if (delayNodeRef.current) {
        delayNodeRef.current.disconnect()
        delayNodeRef.current = null
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect()
        analyserRef.current = null
      }
      freqArrayRef.current = null
      transientDataArraysRef.current = []
      stereoDataArraysRef.current = []
      delayBufferReadPtrRef.current = []
      delayBufferFillLevelRef.current = []
      lastScrollTimeRef.current = []
      scrollRateRef.current = []
      animatedHeightsRef.current = []
      canvasContextRef.current = null
      initializedRef.current = false
    }
  }, [])

  useEffect(() => {
    const delayBufferId = delayBufferIdRef.current
    delayBufferManager.getOrCreateDelayBuffer(delayBufferId)

    return () => {
      delayBufferManager.removeDelayBuffer(delayBufferId)
    }
  }, [])

  useEffect(() => {
    if (!isPlaying) {
      // Reset delay buffers when playback stops
      const delayBuffer = delayBufferManager.getOrCreateDelayBuffer(delayBufferIdRef.current)
      delayBuffer.fillLevel = 0
      delayBuffer.writePtr = 0
      delayBufferReadPtrRef.current.fill(0)
      delayBufferFillLevelRef.current.fill(0)
      lastScrollTimeRef.current.fill(null)
      return
    }

    const ctx = dspscript.getAudioContext()
    const sourceNode = dspscript.getWorkletNode()
    if (!ctx || !sourceNode) return

    // Create analyzers once when first playing
    if (!initializedRef.current) {
      transientDataArraysRef.current = []
      stereoDataArraysRef.current = []

      // Create single analyser for frequency analyzer (type 0) if it doesn't exist
      if (!analyserRef.current) {
        const delayNode = ctx.createDelay(1)
        delayNode.delayTime.value = Number.isFinite(ctx.outputLatency) ? ctx.outputLatency : 0
        delayNodeRef.current = delayNode

        const analyser = ctx.createAnalyser()
        analyser.fftSize = 32768
        analyser.smoothingTimeConstant = 0
        sourceNode.connect(delayNode).connect(analyser)
        analyserRef.current = analyser
        freqArrayRef.current = new Uint8Array(analyser.frequencyBinCount)
      }

      for (let i = 0; i < 4; i++) {
        // Use RingAnalyser for time-domain data (types 1, 2, 3)
        transientDataArraysRef.current.push(new Float32Array(1024))

        // For lissajous (type 3), we need stereo data
        stereoDataArraysRef.current.push({
          left: new Uint8Array(512),
          right: new Uint8Array(512),
        })

        // Delay buffer managed by delayBufferManager (shared across all visualizers)
        delayBufferReadPtrRef.current.push(0)
        delayBufferFillLevelRef.current.push(0)
        lastScrollTimeRef.current.push(null)
        scrollRateRef.current.push(120)

        // Animated heights for frequency analyzer (type 0) - one per bar
        animatedHeightsRef.current.push(new Float32Array(256))
      }

      // Reset ring analyser to resync with audio output
      const ringAnalyser = dspscript.getRingAnalyser()
      if (ringAnalyser) {
        ringAnalyser.reset()
      }

      // Initialize context with willReadFrequently for type 1 (scrolling) that uses getImageData
      const scrollingCanvas = canvasRefs[1].current
      if (scrollingCanvas) {
        const ctx = scrollingCanvas.getContext('2d', { willReadFrequently: true })
        if (ctx) {
          ctx.imageSmoothingEnabled = false
          canvasContextRef.current = ctx
        }
      }

      // Clear canvas for type 1 (scrolling) visualizers when starting
      if (scrollingCanvas && canvasContextRef.current && scrollingCanvas.width > 0 && scrollingCanvas.height > 0) {
        canvasContextRef.current.clearRect(0, 0, scrollingCanvas.width, scrollingCanvas.height)
      }

      initializedRef.current = true
    }

    const animate = () => {
      // Update delay node to match current output latency
      const delayNode = delayNodeRef.current
      if (delayNode) {
        delayNode.delayTime.value = Number.isFinite(ctx.outputLatency) ? ctx.outputLatency : 0
      }

      // Update delay buffer manager (reads from ring analyser once for all components)
      delayBufferManager.update()

      // Background consumption is needed when type 0 is visible alone, or when type 2/3 are visible alone
      // (since type 2/3 use type 1's read pointer but don't consume samples themselves)
      const type1Index = 1
      const isType0Enlarged = expandedIndexRef.current === 0
      const isType0Mode = modeRef.current === 0
      const isType0Vertical = isVerticalRef.current && verticalIndexRef.current === 1
      const isType2Enlarged = expandedIndexRef.current === 2
      const isType3Enlarged = expandedIndexRef.current === 3
      const isType2Mode = modeRef.current === 2
      const isType3Mode = modeRef.current === 3
      const isType2Vertical = isVerticalRef.current && verticalIndexRef.current === 0

      // Type 1 (scrolling) consumes samples to keep delay buffer flowing when type 0/2/3 are showing alone
      if (isType0Enlarged || isType0Mode || isType0Vertical || isType2Enlarged || isType3Enlarged || isType2Mode
        || isType3Mode || isType2Vertical)
      {
        const delayBuffer = delayBufferManager.getOrCreateDelayBuffer(delayBufferIdRef.current)
        const audioContext = dspscript.getAudioContext()
        const sampleRate = audioContext?.sampleRate ?? 48000
        const delayBufferSize = delayBuffer.left.length
        const delaySamples = Math.floor(sampleRate * 0.1)
        const minFillLevel = delaySamples

        // Update fill level
        delayBufferFillLevelRef.current[type1Index] = delayBuffer.fillLevel

        // Type 1 consumption logic (runs when type 1 is not visible)
        if (delayBuffer.fillLevel >= minFillLevel) {
          const now = performance.now()
          const pixelsPerSecond = scrollRateRef.current[type1Index]
          const pixelsPerMs = pixelsPerSecond / 1000

          // In grid mode, type 1 is the leader - sync to shared read pointer and fill level when returning from enlarged mode
          let didSync = false
          if (modeRef.current === undefined && expandedIndexRef.current === null && !isVerticalRef.current) {
            const needsSync = delayBufferReadPtrRef.current[type1Index] !== delayBuffer.readPtr
            if (needsSync) {
              delayBufferReadPtrRef.current[type1Index] = delayBuffer.readPtr
              delayBufferFillLevelRef.current[type1Index] = delayBuffer.fillLevel
              didSync = true
            }
          }

          if (lastScrollTimeRef.current[type1Index] === null || didSync) {
            lastScrollTimeRef.current[type1Index] = now
          }
          else {
            const elapsedMs = now - lastScrollTimeRef.current[type1Index]!
            const totalPixelsToScroll = Math.floor(elapsedMs * pixelsPerMs)

            if (totalPixelsToScroll > 0) {
              const elapsedSeconds = elapsedMs / 1000
              const samplesForTimePeriod = Math.floor(sampleRate * elapsedSeconds)

              if (delayBufferFillLevelRef.current[type1Index] >= samplesForTimePeriod) {
                const samplesToRead = Math.min(samplesForTimePeriod, delayBufferFillLevelRef.current[type1Index])

                // Consume samples (advance read pointer)
                delayBufferReadPtrRef.current[type1Index] = (delayBufferReadPtrRef.current[type1Index] + samplesToRead)
                  % delayBufferSize
                delayBuffer.readPtr = delayBufferReadPtrRef.current[type1Index]
                // Recalculate fill level from actual distance
                const fillDistance = (delayBuffer.writePtr - delayBuffer.readPtr + delayBufferSize) % delayBufferSize
                delayBuffer.fillLevel = Math.min(fillDistance, delayBufferSize)
                delayBufferFillLevelRef.current[type1Index] = delayBuffer.fillLevel

                // In grid mode, sync all visualizer read pointers
                if (modeRef.current === undefined && expandedIndexRef.current === null && !isVerticalRef.current) {
                  for (let j = 0; j < delayBufferReadPtrRef.current.length; j++) {
                    delayBufferReadPtrRef.current[j] = delayBuffer.readPtr
                  }
                }

                lastScrollTimeRef.current[type1Index] = now
              }
            }
          }
        }
        else {
          if (lastScrollTimeRef.current[type1Index] !== null) {
            lastScrollTimeRef.current[type1Index] = null
          }
        }
      }

      canvasRefs.forEach((ref, i) => {
        const canvas = ref.current
        if (!canvas) return

        // If mode is specified, only render the canvas matching that mode
        if (modeRef.current !== undefined && i % 4 !== modeRef.current) return

        // If a canvas is expanded, only render that canvas
        if (expandedIndexRef.current !== null && i !== expandedIndexRef.current) return

        // In vertical mode, only render the current canvas
        if (isVerticalRef.current) {
          const currentCanvasIndex = [2, 0][verticalIndexRef.current] ?? -1
          if (i !== currentCanvasIndex) return
        }

        const ctx = i % 4 === 1 && canvasContextRef.current
          ? canvasContextRef.current
          : canvas.getContext('2d')
        if (!ctx) return
        ctx.imageSmoothingEnabled = false

        const type = i % 4
        const transientDataArray = transientDataArraysRef.current[i]
        const stereoData = stereoDataArraysRef.current[i]

        // Type 0 (frequency) uses freqArray, types 1-3 use transientDataArray/stereoData
        if (type !== 0 && (!transientDataArray || !stereoData)) {
          return
        }

        if (type === 0) {
          // Frequency bars (logarithmic scale) with gravity animation
          const analyser = analyserRef.current
          const freqArray = freqArrayRef.current
          if (!analyser || !freqArray) return
          analyser.getByteFrequencyData(freqArray as any)

          ctx.clearRect(0, 0, canvas.width, canvas.height)

          const minFreq = 20
          const maxFreq = 20000
          const barCount = Math.min(freqArray.length, 256)
          const barWidth = canvas.width / barCount

          const animatedHeights = animatedHeightsRef.current[i]
          if (!animatedHeights) return

          const gravity = 0.025 // Gravity fall rate per frame
          const barHeights: number[] = []

          for (let j = 0; j < barCount; j++) {
            const t = j / barCount
            const freq = minFreq * Math.pow(maxFreq / minFreq, t)
            const exactBinIndex = (freq / (analyser.context.sampleRate / 2)) * freqArray.length
            const binIndex = Math.floor(exactBinIndex)
            const binFraction = exactBinIndex - binIndex

            const clampedIndex = Math.max(0, Math.min(binIndex, freqArray.length - 1))
            const nextIndex = Math.min(clampedIndex + 1, freqArray.length - 1)

            const value1 = freqArray[clampedIndex]! / 255
            const value2 = freqArray[nextIndex]! / 255
            const interpolatedValue = value1 + (value2 - value1) * binFraction

            const targetHeight = interpolatedValue * canvas.height
            const currentAnimatedHeight = animatedHeights[j] ?? 0

            // If target is higher, push up to target; otherwise apply gravity
            const newHeight = targetHeight > currentAnimatedHeight
              ? targetHeight
              : Math.max(0, currentAnimatedHeight - gravity * canvas.height)

            animatedHeights[j] = newHeight
            barHeights.push(newHeight)

            const x = j * barWidth
            const y = canvas.height - newHeight
            ctx.fillStyle = '#777'
            ctx.fillRect(x, y, Math.max(1, barWidth + 1), newHeight)
          }

          // Draw moving average curve in white over bars
          ctx.save()
          ctx.strokeStyle = '#ddd'
          ctx.lineWidth = isVerticalRef.current ? 4 : 2
          ctx.beginPath()
          let isStarted = false
          for (let j = 0; j < barCount; j++) {
            // Moving average over window centered at j
            let sum = 0
            let count = 0
            const movingAvgWindow = 8
            const halfWin = Math.floor(movingAvgWindow / 2)
            for (let k = Math.max(0, j - halfWin); k <= Math.min(barCount - 1, j + halfWin); k++) {
              sum += barHeights[k]
              count++
            }
            const avgHeight = sum / count
            const x = j * barWidth + barWidth / 2
            const y = canvas.height - avgHeight
            if (!isStarted) {
              ctx.moveTo(x, y)
              isStarted = true
            }
            else {
              ctx.lineTo(x, y)
            }
          }
          ctx.stroke()
          ctx.restore()
        }
        else if (type === 1) {
          // Amplitude meter (scrolling) - constant speed with delay buffer
          const delayBuffer = delayBufferManager.getOrCreateDelayBuffer(delayBufferIdRef.current)
          const audioContext = dspscript.getAudioContext()
          const sampleRate = audioContext?.sampleRate ?? 48000
          const delayBufferSize = delayBuffer.left.length
          const delaySamples = Math.floor(sampleRate * 0.1)
          const minFillLevel = delaySamples

          // In grid mode, type 1 is the leader - sync to shared read pointer when returning from enlarged mode
          let needsTimerReset = false
          if (modeRef.current === undefined && expandedIndexRef.current === null && !isVerticalRef.current) {
            if (delayBufferReadPtrRef.current[i] !== delayBuffer.readPtr) {
              delayBufferReadPtrRef.current[i] = delayBuffer.readPtr
              needsTimerReset = true
            }
          }

          // Calculate available samples (distance from read to write, wrapping around)
          const writePtr = delayBuffer.writePtr
          const distance = (writePtr - delayBufferReadPtrRef.current[i] + delayBufferSize) % delayBufferSize
          const availableSamples = Math.min(distance, delayBufferSize)

          // Wait for delay buffer to fill before starting to scroll
          if (availableSamples < minFillLevel) {
            if (lastScrollTimeRef.current[i] !== null) {
              lastScrollTimeRef.current[i] = null
            }
            return
          }

          const now = performance.now()
          const pixelsPerSecond = scrollRateRef.current[i]
          const pixelsPerMs = pixelsPerSecond / 1000

          if (lastScrollTimeRef.current[i] === null || needsTimerReset) {
            lastScrollTimeRef.current[i] = now
            return
          }

          const elapsedMs = now - lastScrollTimeRef.current[i]!

          // If elapsed time is too large, reset timer
          if (elapsedMs > 1000) {
            lastScrollTimeRef.current[i] = now
            return
          }

          const pixelsToScroll = Math.floor(elapsedMs * pixelsPerMs)

          if (pixelsToScroll <= 0) {
            return
          }

          // Calculate how many samples correspond to this time period
          const elapsedSeconds = elapsedMs / 1000
          const samplesForTimePeriod = Math.floor(sampleRate * elapsedSeconds)

          // Only read what's available
          if (availableSamples < samplesForTimePeriod) {
            return
          }

          const samplesToRead = Math.min(samplesForTimePeriod, availableSamples)

          // Scroll canvas
          if (canvas.width > pixelsToScroll) {
            const imageData = ctx.getImageData(pixelsToScroll, 0, canvas.width - pixelsToScroll, canvas.height)
            ctx.putImageData(imageData, 0, 0)
            ctx.clearRect(canvas.width - pixelsToScroll, 0, pixelsToScroll, canvas.height)
          }
          else {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
          }

          // Draw new pixels at the right edge, distributing samples across pixels
          const samplesPerPixel = samplesToRead / pixelsToScroll
          for (let p = 0; p < pixelsToScroll; p++) {
            const x = canvas.width - pixelsToScroll + p
            if (x < 0 || x >= canvas.width) continue

            const sampleStart = Math.floor(p * samplesPerPixel)
            const sampleEnd = p === pixelsToScroll - 1
              ? samplesToRead
              : Math.floor((p + 1) * samplesPerPixel)
            let peak = 0

            for (let s = sampleStart; s < sampleEnd && s < samplesToRead; s++) {
              const readIdx = (delayBufferReadPtrRef.current[i] + s) % delayBufferSize
              const sample = (delayBuffer.left[readIdx]! + delayBuffer.right[readIdx]!) * 0.5
              peak = Math.max(peak, Math.abs(sample))
            }

            if (Number.isFinite(peak)) {
              const ampBarHeight = Math.max(1, peak * canvas.height)
              const ampY = (canvas.height - ampBarHeight) / 2

              if (Number.isFinite(ampBarHeight) && Number.isFinite(ampY)) {
                const grad = ctx.createLinearGradient(x, ampY, x, ampY + ampBarHeight)
                if (peak > 1) {
                  grad.addColorStop(0, '#f00')
                  grad.addColorStop(0.5, '#f00')
                  grad.addColorStop(1, '#f00')
                }
                else {
                  grad.addColorStop(0.2, '#999')
                  grad.addColorStop(0.5, '#ddd')
                  grad.addColorStop(0.8, '#999')
                }
                ctx.fillStyle = grad
                ctx.fillRect(x, ampY, 1, ampBarHeight)
              }
            }
          }

          // Update read pointer and recalculate fill level
          delayBufferReadPtrRef.current[i] = (delayBufferReadPtrRef.current[i] + samplesToRead) % delayBufferSize
          delayBuffer.readPtr = delayBufferReadPtrRef.current[i]
          // Recalculate fill level from actual distance
          const fillDistance = (delayBuffer.writePtr - delayBuffer.readPtr + delayBufferSize) % delayBufferSize
          delayBuffer.fillLevel = Math.min(fillDistance, delayBufferSize)
          delayBufferFillLevelRef.current[i] = delayBuffer.fillLevel

          // In grid mode, type 1 is the leader - sync all visualizer read pointers
          if (modeRef.current === undefined && expandedIndexRef.current === null && !isVerticalRef.current) {
            for (let j = 0; j < delayBufferReadPtrRef.current.length; j++) {
              delayBufferReadPtrRef.current[j] = delayBuffer.readPtr
            }
          }

          lastScrollTimeRef.current[i] = now
        }
        else if (type === 2) {
          // Waveform - always use type 1's read pointer (never consume samples)
          const delayBuffer = delayBufferManager.getOrCreateDelayBuffer(delayBufferIdRef.current)
          const audioContext = dspscript.getAudioContext()
          const sampleRate = audioContext?.sampleRate ?? 48000
          const delayBufferSize = delayBuffer.left.length
          const delaySamples = Math.floor(sampleRate * 0.1)
          const minFillLevel = delaySamples

          // Wait for delay buffer to fill (same as scrolling visualizer)
          if (delayBuffer.fillLevel < minFillLevel) {
            return
          }

          // Always use type 1's read pointer (never consume samples)
          const isGridMode = modeRef.current === undefined && expandedIndexRef.current === null
            && !isVerticalRef.current
          const readPtr = isGridMode ? delayBuffer.readPtr : delayBufferReadPtrRef.current[1]

          // Read samples from delay buffer starting at type 1's read pointer
          // Use average of left and right channels
          const samplesNeeded = transientDataArray.length
          const samplesToRead = Math.min(samplesNeeded, delayBuffer.fillLevel)

          for (let s = 0; s < samplesToRead; s++) {
            const readIdx = (readPtr + s) % delayBufferSize
            transientDataArray[s] = (delayBuffer.left[readIdx]! + delayBuffer.right[readIdx]!) * 0.5
          }

          // Fill remaining with zeros if needed
          for (let s = samplesToRead; s < transientDataArray.length; s++) {
            transientDataArray[s] = 0
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height)
          const centerY = canvas.height / 2
          ctx.beginPath()
          ctx.strokeStyle = '#666'
          ctx.lineWidth = isVerticalRef.current ? 1 : 0.75
          ctx.moveTo(0, centerY)
          ctx.lineTo(canvas.width, centerY)
          ctx.stroke()
          ctx.beginPath()
          ctx.strokeStyle = '#ddd'
          ctx.lineJoin = 'bevel'
          ctx.lineCap = 'butt'
          ctx.lineWidth = isVerticalRef.current ? 3 : 1.35
          const triggerLevel = 0.01
          let triggerIndex = 0
          const searchLength = Math.floor(transientDataArray.length / 2)
          for (let j = 0; j < searchLength - 1; j++) {
            if (
              transientDataArray[j]! < triggerLevel
              && transientDataArray[j + 1]! >= triggerLevel
            ) {
              triggerIndex = j
              break
            }
          }
          const amplitude = centerY
          let first = true
          for (let x = 0; x < canvas.width; x++) {
            // Compute the corresponding sample index based on x position
            // Shifted by triggerIndex, then scale x to array
            const relPos = x / canvas.width
            const sampleIndexUnwrapped = triggerIndex + relPos * transientDataArray.length
            const sampleIndex = Math.round(sampleIndexUnwrapped) % transientDataArray.length
            const sample = transientDataArray[sampleIndex]!
            const y = centerY - sample * amplitude
            if (first) {
              ctx.moveTo(x, y)
              first = false
            }
            else {
              ctx.lineTo(x, y)
            }
          }
          ctx.stroke()
        }
        else if (type === 3) {
          // Lissajous XY (L -> X, R -> Y) - always use type 1's read pointer (never consume samples)
          const delayBuffer = delayBufferManager.getOrCreateDelayBuffer(delayBufferIdRef.current)
          const audioContext = dspscript.getAudioContext()
          const sampleRate = audioContext?.sampleRate ?? 48000
          const delayBufferSize = delayBuffer.left.length
          const delaySamples = Math.floor(sampleRate * 0.1)
          const minFillLevel = delaySamples

          // Wait for delay buffer to fill (same as scrolling visualizer)
          if (delayBuffer.fillLevel < minFillLevel) {
            return
          }

          // Always use type 1's read pointer (never consume samples)
          const isGridMode = modeRef.current === undefined && expandedIndexRef.current === null
            && !isVerticalRef.current
          const readPtr = isGridMode ? delayBuffer.readPtr : delayBufferReadPtrRef.current[1]

          // Read samples from delay buffer starting at type 1's read pointer
          const samplesNeeded = Math.min(stereoData.left.length, stereoData.right.length)
          const samplesToRead = Math.min(samplesNeeded, delayBuffer.fillLevel)

          // Convert float samples to byte format (0-255) for lissajous
          for (let s = 0; s < samplesToRead; s++) {
            const readIdx = (readPtr + s) % delayBufferSize
            stereoData.left[s] = Math.floor((delayBuffer.left[readIdx]! + 1) * 127.5)
            stereoData.right[s] = Math.floor((delayBuffer.right[readIdx]! + 1) * 127.5)
          }

          // Fill remaining with center value (128) if needed
          for (let s = samplesToRead; s < stereoData.left.length; s++) {
            stereoData.left[s] = 128
            stereoData.right[s] = 128
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height)

          const cx = canvas.width / 2
          const cy = canvas.height / 2
          const scale = Math.min(cx, cy) * 0.9

          ctx.beginPath()
          ctx.strokeStyle = '#666'
          ctx.lineWidth = isVerticalRef.current ? 1 : 0.75
          // Draw a diagonal line, scaled to the size of the canvas and the scale variable
          ctx.moveTo(cx - scale, cy + scale)
          ctx.lineTo(cx + scale, cy - scale)
          ctx.stroke()

          ctx.beginPath()
          ctx.strokeStyle = '#eeea'
          ctx.lineWidth = 1.35
          const len = Math.min(stereoData.left.length, stereoData.right.length)
          if (len > 1) {
            // Get first point (swap left/right: right becomes X, left becomes Y)
            let prevX = (stereoData.right[0]! - 128) / 128
            let prevY = (stereoData.left[0]! - 128) / 128
            let prevPx = cx + prevX * scale
            let prevPy = cy - prevY * scale
            ctx.moveTo(prevPx, prevPy)

            // Use quadraticCurveTo between each triplet of points for smoothness
            for (let j = 1; j < len - 1; j++) {
              const currX = (stereoData.right[j]! - 128) / 128
              const currY = (stereoData.left[j]! - 128) / 128
              const currPx = cx + currX * scale
              const currPy = cy - currY * scale

              const nextX = (stereoData.right[j + 1]! - 128) / 128
              const nextY = (stereoData.left[j + 1]! - 128) / 128
              const nextPx = cx + nextX * scale
              const nextPy = cy - nextY * scale

              // Control point: current, End: midpoint between curr and next
              const ctrlX = currPx
              const ctrlY = currPy
              const endX = (currPx + nextPx) / 2
              const endY = (currPy + nextPy) / 2
              ctx.quadraticCurveTo(ctrlX, ctrlY, endX, endY)

              prevPx = currPx
              prevPy = currPy
            }
            // Connect last segment as a line to the last point
            const lastX = (stereoData.right[len - 1]! - 128) / 128
            const lastY = (stereoData.left[len - 1]! - 128) / 128
            const lastPx = cx + lastX * scale
            const lastPy = cy - lastY * scale
            ctx.lineTo(lastPx, lastPy)
          }
          ctx.stroke()
        }
      })
    }

    const unregister = animationManager.register(animate)

    return () => {
      unregister()
    }
  }, [isPlaying, hoveredFreq, mode])

  const handleCanvasClick = (index: number) => {
    setExpandedIndex(prev => (prev === index ? null : index))
  }

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>, canvasIndex: number) => {
    const type = canvasIndex % 4
    if (type !== 0) return

    const canvas = canvasRefs[canvasIndex].current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const width = rect.width

    const analyser = analyserRef.current
    if (!analyser) return

    const ctx = dspscript.getAudioContext()
    if (!ctx) return

    const minFreq = 20
    const maxFreq = 20000
    const t = x / width
    const hz = minFreq * Math.pow(maxFreq / minFreq, t)

    setHoveredFreq({ index: canvasIndex, hz })
  }

  const handleCanvasMouseLeave = () => {
    setHoveredFreq(null)
  }

  const freqLabel = hoveredFreq && (
    <div
      className="absolute bottom-0 left-1 text-xs font-mono text-white pointer-events-none"
      style={{ textShadow: '1px 1px #000' }}
    >
      {hoveredFreq.hz.toFixed(0)} Hz
    </div>
  )

  if (expandedIndex !== null) {
    const showFreqLabel = hoveredFreq && hoveredFreq.index === expandedIndex && expandedIndex % 4 === 0
    return (
      <div ref={containerRef} className="w-full h-full bg-black relative">
        <canvas
          ref={canvasRefs[expandedIndex]}
          className="w-full h-full bg-black cursor-crosshair"
          onClick={() => handleCanvasClick(expandedIndex)}
          onMouseMove={e => handleCanvasMouseMove(e, expandedIndex)}
          onMouseLeave={handleCanvasMouseLeave}
        />
        {showFreqLabel && freqLabel}
      </div>
    )
  }

  if (layout === 'vertical') {
    if (verticalIndex === 2) {
      return (
        <div
          ref={containerRef}
          className="w-full h-full bg-black relative cursor-pointer"
          onClick={cycleVerticalIndex}
        />
      )
    }
    const currentCanvasIndex = [2, 0][verticalIndex]
    const showFreqLabel = hoveredFreq && hoveredFreq.index === currentCanvasIndex && currentCanvasIndex % 4 === 0
    return (
      <div
        ref={containerRef}
        className="w-full h-full bg-black relative cursor-pointer"
        onClick={cycleVerticalIndex}
      >
        <canvas
          ref={canvasRefs[currentCanvasIndex]}
          className="w-full h-full bg-black"
          onClick={e => {
            e.stopPropagation()
            cycleVerticalIndex()
          }}
          onMouseMove={e => handleCanvasMouseMove(e, currentCanvasIndex)}
          onMouseLeave={handleCanvasMouseLeave}
        />
        {showFreqLabel && freqLabel}
      </div>
    )
  }

  // If mode is specified, show only that visualizer type
  if (mode !== undefined) {
    const canvasIndex = mode
    const showFreqLabel = hoveredFreq && hoveredFreq.index === canvasIndex && canvasIndex % 4 === 0
    return (
      <div ref={containerRef} className="w-full h-full bg-black relative">
        <canvas
          ref={canvasRefs[canvasIndex]}
          className="w-full h-full bg-black"
          onMouseMove={e => handleCanvasMouseMove(e, canvasIndex)}
          onMouseLeave={handleCanvasMouseLeave}
        />
        {showFreqLabel && freqLabel}
      </div>
    )
  }

  const showFreqLabel = hoveredFreq && hoveredFreq.index % 4 === 0
  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-black p-[1px] grid grid-cols-2 grid-rows-2 gap-[1px] relative"
    >
      <canvas
        ref={canvasRefs[1]}
        className="w-full h-full bg-black cursor-pointer"
        onClick={() => handleCanvasClick(1)}
        onMouseMove={e => handleCanvasMouseMove(e, 1)}
        onMouseLeave={handleCanvasMouseLeave}
      />
      <canvas
        ref={canvasRefs[2]}
        className="w-full h-full bg-black cursor-pointer"
        onClick={() => handleCanvasClick(2)}
        onMouseMove={e => handleCanvasMouseMove(e, 2)}
        onMouseLeave={handleCanvasMouseLeave}
      />
      <canvas
        ref={canvasRefs[0]}
        className="w-full h-full bg-black cursor-pointer"
        onClick={() => handleCanvasClick(0)}
        onMouseMove={e => handleCanvasMouseMove(e, 0)}
        onMouseLeave={handleCanvasMouseLeave}
      />
      <canvas
        ref={canvasRefs[3]}
        className="w-full h-full bg-black cursor-pointer"
        onClick={() => handleCanvasClick(3)}
        onMouseMove={e => handleCanvasMouseMove(e, 3)}
        onMouseLeave={handleCanvasMouseLeave}
      />
      {showFreqLabel && freqLabel}
    </div>
  )
})
